import { fetchUtils } from 'react-admin';
import jsonServerProvider from 'ra-data-json-server';
import config from './config/config';


// Get the authentication token from local storage if it exists
const getAuthToken = () => {
    const auth = localStorage.getItem('auth');
    if (auth) {
        const { access_token } = JSON.parse(auth);
        return access_token;
    }
    throw new Error('No authentication token found');
};

// An authenticated HTTP client that includes the Authorization header
const authenticatedHttpClient = (url, options = {}) => {
    const token = getAuthToken();
    
    // Merge any existing headers with our authentication header
    const authenticatedOptions = {
        ...options,
        headers: new Headers({
            ...options.headers,
            Authorization: `Bearer ${token}`,
        }),
    };
    
    return fetchUtils.fetchJson(url, authenticatedOptions);
};

const httpClient = authenticatedHttpClient; // Use the authenticated client for all requests
const dataProvider = jsonServerProvider(config.verifyProApiUrl, authenticatedHttpClient);

const getAccountId = () => {
    const auth = localStorage.getItem('auth');
    if (auth) {
        const { tenant } = JSON.parse(auth);
        return tenant;
    }
    throw new Error('No authentication data found');
};

const getOneProvenance = async (resource, params) => {
    if (resource !== 'provenance') {
        return dataProvider.getOne(resource, params);
    }

    const { id } = params;
    const accountId = getAccountId();
    const url = `${config.verifyProApiUrl}/provenance/${accountId}/${id}`;

    const { json } = await httpClient(url);

    if (json.data && json.data.image) {
        json.data.image = `${config.s3BaseUrl}/${json.data.image}`;
    }

    return {
        data: json
    };
};

const updateProvenance = async (resource, params) => {
    if (resource !== 'provenance') {
        return dataProvider.update(resource, params);
    }

    const { id, data } = params;
    const accountId = getAccountId();
    const url = `${config.verifyProApiUrl}/provenance/${accountId}/${id}`;

    // If a new image file was dropped, send as multipart (same as create)
    if (data.picture && data.picture.rawFile instanceof File) {
        const formData = new FormData();
        formData.append('data', JSON.stringify(data.data));

        const pictureForBackend = {
            src: data.picture.src,
            title: data.picture.title,
            rawFile: {
                path: (data.picture.rawFile.path || data.picture.title).replace(/^\.\//, ''),
                relativePath: (data.picture.rawFile.relativePath || data.picture.title).replace(/^\.\//, ''),
            },
        };
        formData.append('picture', JSON.stringify(pictureForBackend));
        formData.append('file', data.picture.rawFile);

        const response = await fetch(url, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${getAuthToken()}` },
            body: formData,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const json = await response.json();
        return { data: { ...json, id: json.provenance_id } };
    }

    // No new image — send JSON as before
    const { json } = await httpClient(url, {
        method: 'PUT',
        body: JSON.stringify(data),
    });

    return { ...json, id: json.provenance_id };
};

const createProvenance = async (resource, params) => {
    if (resource !== 'provenance') {
        return dataProvider.create(resource, params);
    }

    const { data } = params;
    const accountId = getAccountId();
    const url = `${config.verifyProApiUrl}/provenance/${accountId}`;


    console.log("data: ")
    for (let [key, value] of Object.entries(data)) {
        console.log(`data.${key}: ${value}`);
    }
    console.log("data.picture: ")
    for (let [key, value] of Object.entries(data.picture)) {
        console.log(`data.picture.${key}: ${value}`);
    }

    // FormData allows sending files and form fields together as multipart/form-data
    const formData = new FormData();



    // Append the form data
    formData.append('data', JSON.stringify(data.data));

    // Append the picture data. The backend uses picture.rawFile.path to build the S3 key.
    // When a file is drag-dropped, the browser sets path to "./filename.png" (with leading "./"),
    // which corrupts the S3 key to e.g. "abc123_./filename.png". Strip the "./" prefix.
    const pictureForBackend = {
        src: data.picture.src,
        title: data.picture.title,
        rawFile: data.picture.rawFile ? {
            path: (data.picture.rawFile.path || data.picture.title).replace(/^\.\//, ''),
            relativePath: (data.picture.rawFile.relativePath || data.picture.title).replace(/^\.\//, ''),
        } : undefined,
    };
    formData.append('picture', JSON.stringify(pictureForBackend));

    // Append the image file from params.data.picture.rawFile
    if (data.picture && data.picture.rawFile) {
        formData.append('file', data.picture.rawFile);
    }

    console.log("formData: ")
    for (let [key, value] of formData.entries()) {
        console.log(`formData.${key}: ${value}`);
    }

    // Use native fetch instead of httpClient (fetchUtils.fetchJson).
    // fetchUtils.fetchJson sets Content-Type: application/json, overriding the browser's
    // auto-generated Content-Type: multipart/form-data; boundary=... that FormData requires.
    // DO NOT set Content-Type manually here — the browser must set it with the boundary.
    const response = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        body: formData,
    });
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }
    const json = await response.json();

    return { data: json };
};

const listProvenance = async (resource, params) => {
    if (resource !== 'provenance') {
        return dataProvider.getList(resource, params);
    }

    const { pagination, sort, filter } = params;
    const accountId = getAccountId();

    const query = {
        page: pagination.page,
        perPage: pagination.perPage,
        sortField: sort.field,
        sortOrder: sort.order,
        filter: JSON.stringify(filter),
    };

    const queryString = new URLSearchParams(query).toString();
    const url = `${config.verifyProApiUrl}/provenance/${accountId}?${queryString}`;

    const { json } = await httpClient(url);

    // Map provenance_id to id for each item
    const data = json.data.map(item => ({
        ...item,
        id: item.provenance_id,
        image: item.image ? `${config.s3BaseUrl}/${item.image}` : 'null' // concatenate image S3 path with the S3 bucket hostname
    }));

    return {
        data: data,
        total: data.length,
    };
};

const listProvisioning = async (resource, params) => {
    if (resource !== 'provisioning') {
        return dataProvider.getList(resource, params);
    }

    const { pagination, sort, filter } = params;
    const query = {
        page: pagination.page,
        perPage: pagination.perPage,
    };

    const url = `${config.verifyProApiUrl}/provisioning?${new URLSearchParams(query)}`;

    const { json } = await httpClient(url);

    return {
        data: json.data,
        total: json.total,
    };
};

const uploadProvenanceCSV = async (resource, params) => {
    console.log('Starting uploadProvenanceCSV with params:', {
        resource,
        paramsId: params.id,
        hasData: !!params.data,
        hasFile: !!(params.data && params.data.get('file'))
    });

    const { id, data } = params;
    if (!data || !data.get('file')) {
        console.error('Upload failed: No file provided in FormData');
        throw new Error('No file provided');
    }

    const accountId = getAccountId();
    console.log('Account ID:', accountId);

    const file = data.get('file');
    console.log('File details:', {
        name: file.name,
        size: file.size,
        type: file.type
    });

    const url = `${config.verifyProApiUrl}/provenance/upload/${accountId}/${id}`;
    console.log('Uploading to URL:', url);

    try {
        console.log('Initiating fetch request with content-type: text/csv');

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'text/csv',
            },
            body: file,
        });

        console.log('Received response:', {
            status: response.status,
            statusText: response.statusText,
            headers: Object.fromEntries(response.headers.entries())
        });

        if (!response.ok) {
            const error = await response.json();
            console.error('Response not OK:', error);
            throw new Error(error.message || 'Upload failed');
        }

        const json = await response.json();
        console.log('Successfully parsed response:', json);

        // Transform the response to match React Admin's expected format
        return {
            data: {
                id: id, // Use the provenance_id we passed in
                ...json // Include the rest of the response
            }
        };
    } catch (error) {
        console.error('Upload error:', {
            name: error.name,
            message: error.message,
            stack: error.stack
        });
        throw error;
    }
};


// Store last timestamps for cursor-based pagination
const pageTimestamps = new Map();

const listVerifications = async (resource, params) => {
    if (resource !== 'verifications') {
        return dataProvider.getList(resource, params);
    }

    const { pagination, sort, filter } = params;
    const accountId = getAccountId();

    // Build query parameters
    const query = new URLSearchParams();

    // Pagination
    if (pagination?.page) query.set('page', pagination.page.toString());
    if (pagination?.perPage) query.set('perPage', pagination.perPage.toString());

    // Cursor-based pagination: add after_timestamp for pages > 1
    if (pagination?.page > 1) {
        const pageKey = `${accountId}_${JSON.stringify(filter)}_${pagination.perPage}`;
        const lastTimestamp = pageTimestamps.get(`${pageKey}_${pagination.page - 1}`);
        if (lastTimestamp) {
            query.set('after_timestamp', lastTimestamp);
        }
    }

    // Filtering
    if (filter?.uid) query.set('filter', `uid=${filter.uid}`);
    if (filter?.is_valid !== undefined) query.set('filter', `is_valid=${filter.is_valid}`);
    if (filter?.is_tampered && filter.is_tampered !== 'both' && filter.is_tampered !== 'all' && filter.is_tampered !== '') query.set('filter', `is_tampered=${filter.is_tampered}`);
    
    // Handle date filtering - convert YYYY-MM-DD to YYYY-MM-DDTHH:MM:SS if needed
    if (filter?.start_time) {
        const startTime = filter.start_time.includes('T') 
            ? filter.start_time 
            : `${filter.start_time}T00:00:00`;
        query.set('start_time', startTime);
    }
    if (filter?.end_time) {
        const endTime = filter.end_time.includes('T') 
            ? filter.end_time 
            : `${filter.end_time}T23:59:59`;
        query.set('end_time', endTime);
    }

    // Sorting
    if (sort?.field) {
        query.set('sort', sort.field);
        query.set('order', sort.order.toLowerCase());
    }

    const url = `${config.verifyProApiUrl}/log/${accountId}?${query.toString()}`;

    const { json } = await httpClient(url);

    const processedData = json.data.map(item => ({
        ...item,
        id: `${item.uid}_${item.timestamp}`, // Create a composite ID
    }));

    // Store the last timestamp for next page cursor
    if (processedData.length > 0) {
        const pageKey = `${accountId}_${JSON.stringify(filter)}_${pagination.perPage}`;
        const lastTimestamp = processedData[processedData.length - 1].timestamp;
        pageTimestamps.set(`${pageKey}_${pagination.page}`, lastTimestamp);
    }

    return {
        data: processedData,
        // Set high total for CloudWatch data to enable pagination through all available records
        total: 1000,
    };
};

const getOneVerification = async (resource, params) => {
    if (resource !== 'verifications') {
        return dataProvider.getOne(resource, params);
    }

    // Extract uid and timestamp from composite id
    const [uid, timestamp] = params.id.split('_');
    const accountId = getAccountId();

    const query = new URLSearchParams();
    query.set('filter', `uid=${uid}`);
    // if (timestamp) {
    //     query.set('start_time', timestamp);
    //     query.set('end_time', timestamp);
    // }

    const url = `${config.verifyProApiUrl}/log/${accountId}?${query.toString()}`;

    const { json } = await httpClient(url);

    if (!json.data || json.data.length === 0) {
        throw new Error('Verification record not found');
    }

    return {
        data: {
            ...json.data[0],
            id: params.id,
        },
    };
};

const NFTList = async (resource, params) => {
    if (resource !== 'nft') { // default to dataProvider if the resource is not 'nft'
        return dataProvider.getList(resource, params);
    }

    const { pagination, sort, filter } = params;
    const accountId = getAccountId();

    const query = {
        page: pagination.page,
        perPage: pagination.perPage,
        sortField: sort.field,
        sortOrder: sort.order,
        filter: JSON.stringify(filter),
    };

    const queryString = new URLSearchParams(query).toString();
    const url = `${config.verifyProApiUrl}/nft-collections/${accountId}?${queryString}`;

    const { json } = await httpClient(url);
    console.log("json: ", json)

    // Map provenance_id to id for each item
    const data = json.data.map(item => ({
        ...item,
        id: item.provenance_id,
        image: item.image ? `${config.s3BaseUrl}/${item.image}` : 'null' // concatenate image S3 path with the S3 bucket hostname
    }));
    // console.log("data: ", data)

    return {
        data: data,
        total: data.length,
    };
}

const customDataProvider = {
    ...dataProvider,
    getOne: (resource, params) => {
        switch (resource) {
            case 'verifications':
                return getOneVerification(resource, params);
            case 'provenance':
                return getOneProvenance(resource, params);
            default:
                return dataProvider.getOne(resource, params);
        }
    },
    getList: (resource, params) => {
        switch (resource) {
            case 'provisioning':
                return listProvisioning(resource, params);
            case 'provenance':
                return listProvenance(resource, params);
            case 'verifications':
                return listVerifications(resource, params);
            case 'nft':
                return NFTList(resource, params);
            default:
                return dataProvider.getList(resource, params);
        }
    },
    update: (resource, params) => {
        if (resource === 'provenance') {
            return updateProvenance(resource, params);
        }
        return dataProvider.update(resource, params);
    },
    create: (resource, params) => {
        if (resource === 'provenance/upload') {
            return uploadProvenanceCSV(resource, params);
        }
        if (resource === 'provenance') {
            return createProvenance(resource, params);
        }
        return dataProvider.create(resource, params);
    },
};

const mintNFTs = async () => {
    try {
        const accountId = getAccountId();
        if (!accountId) {
            throw new Error('No account ID found in local storage');
        }
        // Log the account ID for debugging
        console.log('Minting NFTs for account:', accountId);

        // Make the API call
        const response = await fetch(`${config.verifyProApiUrl}/nft-mint-next/${accountId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to mint NFTs: ${response.statusText}`);
        }

        const result = await response.json();
        console.log('NFTs minted successfully:', result);

        // Optionally, refresh the data or show a success message
        alert('NFTs are being minting in the background. You can check the status later.');
    } catch (error) {
        console.error('Error minting NFTs:', error);
        alert('Failed to mint NFTs. Please try again.');
    }
};

export { getAccountId, mintNFTs };
export default customDataProvider;